import Cookies from 'js-cookie';
import { useEffect, useState } from 'react';
import {
    getLocalIdentity,
    getLocalRecentIdentity,
    removeLocalIdentity,
    removeLocalRecentIdentity,
    setLocalIdentity,
    setLocalOriginUid,
    setMemoryIdentity,
} from '@meitu/xdesign-identity';
import { getConfig } from '@/configProvider';
import { removeLocalUid, setLocalUid } from '@/utils/uidKit';
import { IdentityType, ITeamInfo, TeamSeatStatus } from '../types/teamInfo';
import { checkIsBeforeUnload } from '../utils/browserKit';
import eventBus from '../utils/eventBus';
import { fetchTeamIdentityList, fetchTeamMemberInfo } from './api';
import { HIDE_EXPIRED_MODAL, SHOW_EXPIRED_MODAL } from './expiredModal';
import { HIDE_FREEZE_MODAL, SHOW_FREEZE_MODAL } from './freezeModal';
import {
    HIDDEN_IDENTITY_SWITCH_MODAL,
    SHOW_IDENTITY_SWITCH_MODAL,
    SHOW_IDENTITY_SWITCH_MODAL_EVENT_DATA,
} from './loginIdentitySwitchModal';
import { ITeamInfoModal } from './type';
import { getEnterpriseIDKey, init } from './utils';

let teamInfoData: ITeamInfo | null = null;
// 标识是否该项目是否用户主动切换身份动作
let isSwitchIdentityInitiative = false;
/** 标识是否该项目是否用户主动切换身份动作 */
export const getIsSwitchIdentityInitiative = () => isSwitchIdentityInitiative;
// 用来控制业务是否开启身份变化监听回掉
let identityChangeListenerStatus: boolean = false;
/** 用来控制业务是否开启身份变化监听回掉 */
export const getIdentityChangeListenerStatus = () => identityChangeListenerStatus;
let hasFetchTeamInfo: boolean = false;

// 刷新是否获取团队信息状态事件
const REFRESH_FETCH_TEAM_INFO_STATUS = 'xdesign-widget-refresh-fetch-team-info-status';
// 刷新团队信息事件
const REFRESH_TEAM_INFO = 'xdesign-widget-refresh-team-info';
// 团队身份变更事件
export const CHANGE_TEAM_OID = 'xdesign-widget-change-team-oid';

// 开启身份变化监听
export const enableIdentityChangeListener = () => {
    identityChangeListenerStatus = true;
};

// 关闭身份变化监听
export const disableIdentityChangeListener = () => {
    identityChangeListenerStatus = false;
};

export const changeIsSwitchIdentityInitiativeState = (val: boolean) => {
    isSwitchIdentityInitiative = val;
};

// 定制企业sso授权相关逻辑初始化
export const enterpriseInit = init;

// 获取enterprise企业标识
export const getEnterpriseID = () => {
    const enterpriseID = Cookies.get(getEnterpriseIDKey()) || '';
    return enterpriseID;
};

// 是否来自站酷的团队
export const isFromZhanKu = () => !!getLocalIdentity()?.oid && getEnterpriseID() === 'zhanku';

/** 刷新团队信息 */
export const fetchTeamInfo = async () => {
    let local = getLocalIdentity();
    let changeIdentity = false;
    if (!local) {
        setLocalIdentity(0, 0, false);
        local = getLocalIdentity();
        changeIdentity = true;
    }
    console.log('===fetchTeamInfo local', local);
    try {
        const userIdentity = await fetchTeamIdentityList();

        const identity = userIdentity.list.find(
            (v) => v.oid === local?.oid && !!v.groups.find((k) => k.id === local?.groupId),
        );

        // 个人身份数据
        const identityPersonal = userIdentity.list.find((v) => v.oid === 0);

        let teamMember;
        if (local?.oid) {
            teamMember = await fetchTeamMemberInfo();
        }

        // 离职身份需要停留在当前页面, 不做身份切换
        if (!identity) {
            identityPersonal && setLocalUid(identityPersonal.ouid);
            setLocalIdentity(0, 0);
            local = getLocalIdentity();
            changeIdentity = true;
        }

        teamInfoData = {
            identity: {
                ...local,
                ...userIdentity,
            },
            member: teamMember,
        } as ITeamInfo;
    } catch (e) {
        console.log(e, '==========e');
        // 页面跳转时, safari会中断请求, 并抛出异常, 这里不能将身份进行切换
        if (checkIsBeforeUnload()) return;
        teamInfoData = null;
        console.log('获取团队信息失败');
        // 如果团队身份列表接口挂了，当前身份角色非本人的话，需要移除本地uid
        local?.oid && removeLocalUid();
        setMemoryIdentity(0, 0);
        setLocalIdentity(0, 0, false);
    }
    eventBus.emit(REFRESH_TEAM_INFO, teamInfoData);
    eventBus.emit(REFRESH_FETCH_TEAM_INFO_STATUS);
    hasFetchTeamInfo = true;
    if (changeIdentity && teamInfoData && teamInfoData.identity.list.length >= 2) {
        eventBus.emit(CHANGE_TEAM_OID);
    }
};

/** 获取团队信息 */
export const getTeamInfo = () => teamInfoData;

/**
 * 仅供仓库内部 demo / 测试使用，直接注入团队信息状态，避免依赖真实接口返回。
 */
export const __unsafeSetTeamInfoForDemo = (data: ITeamInfo | null) => {
    teamInfoData = data;
    hasFetchTeamInfo = true;

    if (data) {
        setLocalIdentity(data.identity.oid, data.identity.groupId, false);
        const currentIdentity = data.identity.list.find(
            (item) => item.oid === data.identity.oid && item.groups[0]?.id === data.identity.groupId,
        );
        if (typeof currentIdentity?.ouid === 'number') {
            setLocalUid(currentIdentity.ouid);
        }
    }

    eventBus.emit(REFRESH_TEAM_INFO, teamInfoData);
    eventBus.emit(REFRESH_FETCH_TEAM_INFO_STATUS);
};

/** 获取团队信息hook */
export const useTeamInfoState = () => {
    const [data, setData] = useState<ITeamInfo | null>(teamInfoData);
    useEffect(() => {
        const refreshHandle = (e: ITeamInfo | null) => {
            setData(e);
        };
        eventBus.addListener(REFRESH_TEAM_INFO, refreshHandle);
        return () => {
            eventBus.removeListener(REFRESH_TEAM_INFO, refreshHandle);
        };
    }, []);
    return data;
};

// 是否请求过团队信息接口标识
export const useHasFetchTeamInfoState = () => {
    const [status, setStatus] = useState(hasFetchTeamInfo);
    useEffect(() => {
        const handleFn = () => {
            setStatus(true);
            hasFetchTeamInfo = true;
        };
        eventBus.addListener(REFRESH_FETCH_TEAM_INFO_STATUS, handleFn);
        return () => {
            eventBus.removeListener(REFRESH_FETCH_TEAM_INFO_STATUS, handleFn);
        };
    }, []);

    return status;
};

/** 查找指定的团队身份 */
export function findTeamIdentity(oid: number, groupId: number, teamInfo?: ITeamInfo | null) {
    const usedTeamInfo = teamInfo ?? getTeamInfo();
    if (!usedTeamInfo) return null;
    return usedTeamInfo.identity.list.find((v) => v.oid === oid && v.groups[0].id === groupId);
}

/** 获取当前团队的身份信息 */
export function useCurrentTeamIdentity() {
    const teamInfo = useTeamInfoState();
    if (!teamInfo) return null;
    return findTeamIdentity(teamInfo.identity.oid, teamInfo.identity.groupId);
}

/** 获取当前团队的身份信息 */
export function getCurrentTeamIdentity(teamInfo?: ITeamInfo | null) {
    const usedTeamInfo = teamInfo ?? getTeamInfo();
    if (!usedTeamInfo) return null;

    return findTeamIdentity(usedTeamInfo.identity.oid, usedTeamInfo.identity.groupId);
}

/** 打开团队过期弹窗 */
export const openTeamExpiredPopup = (args: Omit<ITeamInfoModal, 'onCancel'>) => {
    eventBus.emit(SHOW_EXPIRED_MODAL, args);
};
/** 关闭团队过期弹窗 */
export const closeTeamExpiredPopup = () => {
    eventBus.emit(HIDE_EXPIRED_MODAL);
};

/** 打开团队冻结弹窗 */
export const openTeamFreezePopup = (args: ITeamInfoModal) => {
    eventBus.emit(SHOW_FREEZE_MODAL, args);
};

/** 关闭团队冻结弹窗 */
export const closeTeamFreezePopup = () => {
    eventBus.emit(HIDE_FREEZE_MODAL);
};

/** 打开身份切换弹窗 */
export const openIdentitySwitchPopup = (opts?: SHOW_IDENTITY_SWITCH_MODAL_EVENT_DATA) => {
    eventBus.emit(SHOW_IDENTITY_SWITCH_MODAL, opts ?? {});
};

/** 关闭身份切换弹窗 */
export const closeIdentitySwitchPopup = () => {
    eventBus.emit(HIDDEN_IDENTITY_SWITCH_MODAL);
};

/** 检测当前成员是否已离职 */
export const checkMemberIsResigned = (teamInfo?: ITeamInfo | null) => {
    const usedTeamInfo = teamInfo ?? getTeamInfo();
    if (!usedTeamInfo || !usedTeamInfo.member) return null;
    return usedTeamInfo.member.job_status === 2;
};

/** 检测个人是否冻结 */
export function checkUserSeatIsFreeze(teamInfo?: ITeamInfo | null) {
    const usedTeamInfo = teamInfo ?? getTeamInfo();
    if (!usedTeamInfo || !usedTeamInfo.member) return null;
    return !checkMemberIsResigned(usedTeamInfo) && usedTeamInfo.member.seat_status === TeamSeatStatus.Freeze;
}

/** 检测个人是否有席位 */
export function checkUserHasSeat(teamInfo?: ITeamInfo | null) {
    const usedTeamInfo = teamInfo ?? getTeamInfo();
    if (!usedTeamInfo || !usedTeamInfo.member) return null;
    return usedTeamInfo.member.has_seat && usedTeamInfo.member.seat_status === TeamSeatStatus.Effect;
}

export const checkIsTeam = (teamInfo?: ITeamInfo | null) => {
    const usedTeamInfo = teamInfo ?? getTeamInfo();
    return !!usedTeamInfo?.identity.oid;
};

export const checkTeamIsEnterprise = (teamInfo?: ITeamInfo | null) => {
    const usedTeamInfo = teamInfo ?? getTeamInfo();
    if (!usedTeamInfo) return false;
    const oid = getLocalIdentity()?.oid;
    if (!oid) return false;
    const identity = usedTeamInfo.identity.list.find((item) => item.oid === oid);
    return identity?.type === IdentityType.ENTERPRISE;
};

/** 检测团队状态是否为vip */
export function checkTeamStatusIsVip(teamInfo?: ITeamInfo | null) {
    const usedTeamInfo = teamInfo ?? getTeamInfo();
    if (!usedTeamInfo || !usedTeamInfo.member) return null;
    return usedTeamInfo.member.team.is_vip;
}

export const checkTeamStatusIsFree = (teamInfo?: ITeamInfo | null) => {
    const usedTeamInfo = teamInfo ?? getTeamInfo();
    return checkIsTeam(usedTeamInfo) && !checkTeamStatusIsVip(usedTeamInfo) && !checkTeamIsEnterprise(usedTeamInfo);
};

export const checkTeamHadBuyVip = (teamInfo?: ITeamInfo | null) => {
    const usedTeamInfo = teamInfo ?? getTeamInfo();
    return checkIsTeam(usedTeamInfo) && !!usedTeamInfo?.member?.team.had_buy;
};

/** 清空团队数据 */
export function clearTeamInfo() {
    closeTeamExpiredPopup();
    closeTeamFreezePopup();
    teamInfoData = null;
    eventBus.emit(REFRESH_TEAM_INFO, null);
    removeLocalIdentity();
}

/** 切换个人/团队身份 */
export const changeIdentity = (identity: { ouid: number; oid: number; groupId: number }, teamInfo = getTeamInfo()) => {
    const currentOid = teamInfo?.identity.oid ?? 0;
    const currentGroupId = teamInfo?.identity.groupId ?? 0;
    if (currentOid !== identity.oid || currentGroupId !== identity.groupId) {
        // 标识一定要前置
        changeIsSwitchIdentityInitiativeState(true);
    }

    setLocalUid(identity.ouid);
    // 切换身份同步cookie
    setLocalIdentity(identity.oid, identity.groupId);
};

/**
 * 登录后打开身份切换弹窗的相关逻辑
 * @param originUid 大账号登录后返回的uid
 * @param accessToken 大账号登录后返回的token
 * @returns
 */
export const openIdentitySwitchPopupAfterLogin = async (originUid: number, accessToken: string) => {
    const { isIntl, isTeam } = getConfig();

    console.log(isIntl, 'getConfig=======', isTeam);

    // 海外或者非团队版不走以下逻辑
    if (isIntl || !isTeam) return true;

    if (accessToken) window.localStorage.setItem('access_token', accessToken);

    // 1、如果本地缓存团队身份对应的原始ID(origin_id)与当前登录账号的ID一致，则自动切换成缓存的身份且刷新页面；
    // 2、如果本地无缓存团队身份，则唤起身份切换弹窗：
    //    2.1、如果无团队，则自动走个人身份，无需唤起身份切换弹窗；
    //    2.2、如果有团队，则唤起身份切换弹窗。

    // 更新cookie中的origin_uid
    setLocalOriginUid(originUid);

    // 获取缓存中的最近身份信息
    const info = getLocalRecentIdentity();

    if (info && info.originUid === originUid) {
        // 最近登录的团队身份和当前账号匹配，直接切成最近登录的身份
        changeIdentity({
            oid: info.oid,
            groupId: info.groupId,
            ouid: info.ouid || 0,
        });

        if (info.oid) {
            window.location.reload();
            return false;
        }

        return true;
    }

    removeLocalRecentIdentity();

    // 拉取团队数据接口
    await fetchTeamInfo();
    // 团队身份数据
    const teamIdentity = teamInfoData?.identity.list?.filter((v) => v.oid) || [];

    if (!teamIdentity.length) {
        // 没有创建过团队数据，直接默认走业务
        return true;
    }

    return new Promise((resolve) => {
        openIdentitySwitchPopup({
            showCloseBtn: false,
            hideCreateBtn: true,
            hideSelectedStatus: true,
            onSwitchComplete: ({ oid }) => {
                if (oid) {
                    // 选择的是团队身份，则刷新页面
                    window.location.reload();
                    resolve(false);
                } else {
                    // 选择的是个人身份，则继续走业务
                    resolve(true);
                }
            },
        });
    });
};

export const checkTeamMemberState = (
    _teamInfo?: ITeamInfo | null,
): {
    /** 超额冻结 */
    isExcess: boolean;
    /** 还有空余席位可邀请 */
    hasSeat: boolean;
} => {
    const teamInfo = _teamInfo ?? getTeamInfo();
    const userCount = teamInfo?.identity.list.find((item) => item.oid === getLocalIdentity()?.oid)?.member_num ?? 0;
    /** 可操作席位数 (当前成员数+剩余可邀请席位数) */
    const operableNum = teamInfo?.member?.team.member_detail.operable_num ?? 0;
    return {
        isExcess: userCount > operableNum,
        hasSeat: userCount < operableNum,
    };
};
